import React, { type PropsWithChildren, useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import EditorAddIcon from '@atlaskit/icon/core/migration/add--editor-add';
import CheckIcon from '@atlaskit/icon/core/migration/check-mark--check';
import PreferencesIcon from '@atlaskit/icon/core/migration/customize--preferences';
import { Box, xcss } from '@atlaskit/primitives';
import { B400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { FireScreenAnalytics, fireUIAnalytics } from '@atlassian/analytics-bridge';
import { Flex } from '@atlassian/eoc-flex';
import FocusState from '@atlassian/eoc-focus-state';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import {
	Subscriber as PageStoreSubscriber,
	useActions as usePageActions,
} from '../../controllers/page-store';
import { CreateVariant } from '../../controllers/page-store/state/types';
import { useSectionUiPreferences } from '../../controllers/preferences';
import { getScheduleRightsResource, getSchedulesResource } from '../../services/resources';

import { CreateSchedule } from './create-schedule';
import { Header } from './header';
import { messages } from './messages';
import { Container, ViewOptionText } from './styled';

const addButtonStyles = xcss({
	margin: '0',
	marginRight: 'space.100',
});

// TODO: use eoc-section -> currently causes problems when used with dropdown as action component
const Section = ({ children }: PropsWithChildren<{}>) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intlApi = useIntl();
	const {
		displayDensity,
		scheduleOrder,
		schedulesCollapsed,
		partialUpdate: updateSectionPreferences,
	} = useSectionUiPreferences();
	const { openCreateSchedule } = usePageActions();
	const { data: schedules } = useResource(getSchedulesResource);
	const { data: scheduleRights } = useResource(getScheduleRightsResource);

	const count = useMemo(
		() => (schedules && schedules.schedules?.length > 0 ? schedules.schedules?.length : undefined),
		[schedules],
	);

	useEffect(() => {
		// Forcing to expand if there is no schedule
		if (!count && schedulesCollapsed) {
			updateSectionPreferences({
				key: 'schedulesCollapsed',
				value: false,
			});
		}
	}, [count, schedulesCollapsed, updateSectionPreferences]);

	return (
		<div>
			<Header
				isOpen={!schedulesCollapsed}
				onOpenChange={(isOpen) => {
					updateSectionPreferences({
						key: 'schedulesCollapsed',
						value: !isOpen,
					});
				}}
				title={intlApi.formatMessage(messages.schedules)}
				count={count}
				actions={
					<>
						{scheduleRights?.hasEditRight && (
							<Box xcss={[schedulesCollapsed ? undefined : addButtonStyles]}>
								<Tooltip content={intlApi.formatMessage(messages.createSchedule)} position="bottom">
									<IconButton
										label={intlApi.formatMessage(messages.createNewSchedule)}
										id="create-schedule-button"
										appearance="subtle"
										icon={EditorAddIcon}
										onClick={(_, analyticsEvent) => {
											fireUIAnalytics(analyticsEvent, 'addSchedule');

											openCreateSchedule({
												id: 'create-schedule-button',
											});
										}}
									/>
								</Tooltip>
							</Box>
						)}

						{!schedulesCollapsed && (
							<DropdownMenu<HTMLButtonElement>
								trigger={({ triggerRef, ...props }) => (
									<Tooltip content={intlApi.formatMessage(messages.preferences)} position="bottom">
										<IconButton
											label={intlApi.formatMessage(messages.preferences)}
											{...props}
											onClick={(e) => {
												props.onClick?.(e);
											}}
											appearance="subtle"
											icon={PreferencesIcon}
											ref={triggerRef}
										/>
									</Tooltip>
								)}
							>
								<DropdownItemGroup title={intlApi.formatMessage(messages.displayDensity)}>
									<DropdownItem
										onClick={() => {
											const event = createAnalyticsEvent({});
											fireUIAnalytics(event, 'button clicked', 'slimPreference');

											updateSectionPreferences({
												key: 'displayDensity',
												value: 'slim',
											});
										}}
									>
										<Flex alignItems="center" justifyContent="space-between">
											<ViewOptionText>{intlApi.formatMessage(messages.slim)}</ViewOptionText>
											{displayDensity === 'slim' && (
												<CheckIcon
													spacing="spacious"
													label="Checked"
													color={token('color.icon.selected', B400)}
												/>
											)}
										</Flex>
									</DropdownItem>
									<DropdownItem
										onClick={() => {
											const event = createAnalyticsEvent({});
											fireUIAnalytics(event, 'button clicked', 'comfortPreference');

											updateSectionPreferences({
												key: 'displayDensity',
												value: 'comfort',
											});
										}}
									>
										<Flex alignItems="center" justifyContent="space-between">
											<ViewOptionText>{intlApi.formatMessage(messages.comfort)}</ViewOptionText>
											{displayDensity === 'comfort' && (
												<CheckIcon
													spacing="spacious"
													label="Checked"
													color={token('color.icon.selected', B400)}
												/>
											)}
										</Flex>
									</DropdownItem>
								</DropdownItemGroup>
								<DropdownItemGroup title={intlApi.formatMessage(messages.scheduleOrder)}>
									<DropdownItem
										onClick={() => {
											const event = createAnalyticsEvent({});
											fireUIAnalytics(event, 'button clicked', 'finalPreference');

											updateSectionPreferences({
												key: 'scheduleOrder',
												value: 'finalSchedule',
											});
										}}
									>
										<Flex alignItems="center" justifyContent="space-between">
											<ViewOptionText>
												{intlApi.formatMessage(messages.finalScheduleOnTop)}
											</ViewOptionText>
											{scheduleOrder === 'finalSchedule' && (
												<CheckIcon
													spacing="spacious"
													label="Checked"
													color={token('color.icon.selected', B400)}
												/>
											)}
										</Flex>
									</DropdownItem>
									<DropdownItem
										onClick={() => {
											const event = createAnalyticsEvent({});
											fireUIAnalytics(event, 'button clicked', 'basePreference');

											updateSectionPreferences({
												key: 'scheduleOrder',
												value: 'baseSchedule',
											});
										}}
									>
										<Flex alignItems="center" justifyContent="space-between">
											<ViewOptionText>
												{intlApi.formatMessage(messages.baseScheduleOnTop)}
											</ViewOptionText>
											{scheduleOrder === 'baseSchedule' && (
												<CheckIcon
													spacing="spacious"
													label="Checked"
													color={token('color.icon.selected', B400)}
												/>
											)}
										</Flex>
									</DropdownItem>
								</DropdownItemGroup>
							</DropdownMenu>
						)}
					</>
				}
			/>
			{!schedulesCollapsed && <Container>{children}</Container>}
			<PageStoreSubscriber>
				{({ isCreateScheduleOpen, activeSchedule, createVariant }, { focusSource }) => (
					<FocusState
						// TODO: add close on escape
						isOpen={isCreateScheduleOpen}
						returnFocus={() => {
							focusSource();

							return false;
						}}
					>
						<CreateSchedule
							baseSchedule={activeSchedule}
							variant={createVariant ?? CreateVariant.Create}
						/>
					</FocusState>
				)}
			</PageStoreSubscriber>

			<FireScreenAnalytics componentName="schedules" />
		</div>
	);
};

export { Section };
